import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_POST, API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

// ========================================
// TYPES
// ========================================
export interface ForgotPasswordPayload {
    email?: string;
    phoneNumber?: string;
}

export interface ResetPasswordPayload {
    token: string;
    newPassword: string;
}

export interface ResetTokenInfo {
    firstName: string;
    lastName: string;
}

interface PasswordResetState {
    forgotLoading: boolean;
    resetLoading: boolean;
    tokenInfoLoading: boolean;
    tokenInfo: ResetTokenInfo | null;
    error: string | null;
}

const initialState: PasswordResetState = {
    forgotLoading: false,
    resetLoading: false,
    tokenInfoLoading: false,
    tokenInfo: null,
    error: null,
};

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Initiates a password reset — sends an email or SMS reset link to the user.
 * Uses: POST /api/User/forgot-password
 * Always returns 200 regardless of whether an account exists (backend behaviour).
 */
export const forgotPassword = createAsyncThunk(
    'passwordReset/forgotPassword',
    async (payload: ForgotPasswordPayload, { rejectWithValue }) => {
        try {
            const response = await API_POST(ENDPOINTS.FORGOT_PASSWORD, payload);
            return response;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message ||
                error?.message ||
                'An error occurred while processing your request.'
            );
        }
    }
);

/**
 * Fetches basic user info (first/last name) associated with a reset token.
 * Uses: GET /api/User/reset-token-info?token=...
 */
export const fetchResetTokenInfo = createAsyncThunk(
    'passwordReset/fetchResetTokenInfo',
    async (token: string, { rejectWithValue }) => {
        try {
            const response = await API_FETCH(`${ENDPOINTS.RESET_TOKEN_INFO}?token=${encodeURIComponent(token)}`);
            return response as ResetTokenInfo;
        } catch (error: any) {
            return rejectWithValue(
                error?.message ||
                'Unable to validate reset token.'
            );
        }
    }
);

/**
 * Resets the user's password using the token received via email/SMS link.
 * Uses: POST /api/User/reset-password
 */
export const resetPassword = createAsyncThunk(
    'passwordReset/resetPassword',
    async (payload: ResetPasswordPayload, { rejectWithValue }) => {
        try {
            const response = await API_POST(ENDPOINTS.RESET_PASSWORD, payload);
            return response;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message ||
                error?.message ||
                'An error occurred while resetting your password.'
            );
        }
    }
);

// ========================================
// SLICE
// ========================================
const passwordResetSlice = createSlice({
    name: 'passwordReset',
    initialState,
    reducers: {
        clearPasswordResetError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Forgot password
            .addCase(forgotPassword.pending, (state) => {
                state.forgotLoading = true;
                state.error = null;
            })
            .addCase(forgotPassword.fulfilled, (state) => {
                state.forgotLoading = false;
                state.error = null;
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.forgotLoading = false;
                state.error = action.payload as string;
            })
            // Fetch reset token info
            .addCase(fetchResetTokenInfo.pending, (state) => {
                state.tokenInfoLoading = true;
                state.error = null;
            })
            .addCase(fetchResetTokenInfo.fulfilled, (state, action) => {
                state.tokenInfoLoading = false;
                state.tokenInfo = action.payload;
            })
            .addCase(fetchResetTokenInfo.rejected, (state, action) => {
                state.tokenInfoLoading = false;
                state.tokenInfo = null;
                state.error = action.payload as string;
            })
            // Reset password
            .addCase(resetPassword.pending, (state) => {
                state.resetLoading = true;
                state.error = null;
            })
            .addCase(resetPassword.fulfilled, (state) => {
                state.resetLoading = false;
                state.error = null;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.resetLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearPasswordResetError } = passwordResetSlice.actions;
export default passwordResetSlice.reducer;